<template>
  <system-frame class="data-merge" :title="city ? 'Linnad' : 'Riigid'" section="Andmete korrastamine" :hint="city ? 'Topelt linnade ühendamine: kliendid ja objektid liiguvad alles jäävale linnale.' : 'Topelt riikide ühendamine: kliendid, objektid ja linnad liiguvad alles jäävale riigile.'">
    <!-- Cities and countries merged by hand (server/service/dataMerge.js):
         tick the rows that are the same place, choose the one that stays,
         Ühenda. Layout agreed on the mockup first. -->
    <p class="text-medium-emphasis mb-4" style="max-width: 72ch">
      <template v-if="city">Märgi read, mis on sama koht, ja vajuta Ühenda. Kliendid ja objektid liiguvad alles jäävale linnale (koos selle riigiga), teised read kustutatakse.</template>
      <template v-else>Tee enne riigid korda, siis linnad. Ühendamisel liiguvad kliendid, objektid ja linnad alles jäävale riigile.</template>
    </p>

    <div class="d-flex flex-wrap ga-8 mb-4">
      <div><div class="text-grey-darken-1 text-body-2">{{ city ? 'Linnu' : 'Riike' }}</div><div class="text-h5">{{ rows.length }}</div></div>
      <div><div class="text-grey-darken-1 text-body-2">Kasutamata</div><div class="text-h5">{{ unusedCount }}</div></div>
      <div><div class="text-grey-darken-1 text-body-2">Soovitusi</div><div class="text-h5">{{ groups.length }}</div></div>
    </div>

    <v-alert v-if="cityHint" type="info" variant="tonal" density="compact" class="mb-4" closable @click:close="cityHint = null">
      {{ cityHint }} <router-link :to="{ name: 'system/data/cities' }">Linnad →</router-link>
    </v-alert>

    <card class="mb-6">
      <div class="text-h6">Soovitused</div>
      <p class="text-body-2 text-medium-emphasis mb-3">
        <template v-if="city">Sama nimi eri kirjaviisis (punkt lõpus, küla/alevik/vald). Klõps märgib grupi. Otsustad ise.</template>
        <template v-else>Kõik "Estonia"/"Eesti" kirjaviisid. Read nagu "3", "Tallinn" või "Rae" pole riigid: märgi need käsitsi koos Estoniaga.</template>
      </p>
      <div v-if="groups.length" class="d-flex flex-wrap ga-2">
        <v-chip
          v-for="group in groups"
          :key="group[0].id"
          :color="chip === group[0].id ? 'green' : undefined"
          :variant="chip === group[0].id ? 'tonal' : 'outlined'"
          @click="pickGroup(group)"
        ><b>{{ group[0].title }}</b><span class="ml-1 text-medium-emphasis">×{{ group.length }}</span></v-chip>
      </div>
      <p v-else-if="loaded" class="text-medium-emphasis mb-0">Soovitusi pole. Ülejäänud leiad otsingust.</p>
    </card>

    <card class="mb-6">
      <div class="d-flex flex-wrap align-center ga-4 mb-2">
        <v-text-field v-model="search" label="Otsi nime järgi" density="compact" hide-details clearable prepend-inner-icon="mdi-magnify" style="min-width: 220px; max-width: 420px" @update:model-value="limit = 50" />
        <v-checkbox v-model="onlyUnused" label="ainult kasutamata" density="compact" hide-details />
        <v-btn v-if="selected.length" variant="text" color="primary" @click="clearSelection">Tühista valik</v-btn>
      </div>
      <v-table density="compact" class="data-merge__table">
        <thead>
          <tr>
            <th style="width: 40px" />
            <th>Nimi</th>
            <th v-if="city">Riik</th>
            <th v-else class="text-right">Linnu</th>
            <th class="text-right">Kliente</th>
            <th class="text-right">Objekte</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in shown" :key="row.id" :class="{ 'data-merge__sel': picked.has(row.id) }">
            <td><v-checkbox-btn :model-value="picked.has(row.id)" density="compact" :aria-label="'Vali ' + row.title" @update:model-value="toggle(row, $event)" /></td>
            <td>
              <v-text-field
                v-if="renaming === row.id"
                v-model="renameTitle"
                density="compact"
                hide-details
                autofocus
                style="max-width: 280px"
                @keydown.enter="saveRename(row)"
                @keydown.esc="renaming = null"
                @blur="renaming = null"
              />
              <template v-else>
                <span :class="{ 'text-medium-emphasis': !isUsed(row) }">{{ row.title }}</span>
                <v-chip v-if="!isUsed(row)" size="x-small" class="ml-2">kasutamata</v-chip>
              </template>
            </td>
            <td v-if="city" :class="row.countryId !== protectedId ? 'text-orange-darken-3' : 'text-medium-emphasis'">
              {{ row.countryTitle || '—' }}
              <v-chip v-if="row.countryId !== protectedId" size="x-small" color="orange-darken-3" variant="tonal" class="ml-1">kahtlane</v-chip>
            </td>
            <td v-else class="text-right">{{ row.cities }}</td>
            <td class="text-right">{{ row.clients }}</td>
            <td class="text-right">{{ row.objects }}</td>
            <td class="text-right text-no-wrap">
              <v-btn size="x-small" variant="outlined" icon="mdi-pencil" title="Muuda nime" @mousedown.prevent @click="startRename(row)" />
              <v-btn v-if="canDelete(row)" size="x-small" variant="outlined" color="red" class="ml-1" :loading="deleting === row.id" @click="remove(row)">Kustuta</v-btn>
            </td>
          </tr>
          <tr v-if="loaded && !filtered.length"><td colspan="6" class="text-medium-emphasis">Midagi ei leitud.</td></tr>
        </tbody>
      </v-table>
      <div v-if="filtered.length > limit" class="text-center mt-2">
        <v-btn variant="text" color="primary" @click="limit = Infinity">Näita kõiki ({{ filtered.length }})</v-btn>
      </div>
    </card>

    <card>
      <div class="text-h6 mb-2">Viimased ühendamised</div>
      <p v-if="!recent.length" class="text-medium-emphasis mb-0">Veel pole midagi ühendatud. Iga ühendamine jääb siia: kes, millal, mis millesse ja mitu rida liikus.</p>
      <v-list v-else density="compact" class="py-0">
        <v-list-item v-for="entry in recent" :key="entry.id" class="px-0">
          <span class="text-medium-emphasis mr-2">{{ dateTime(entry.at) }}</span>
          <b>{{ entry.keepTitle }}</b> ← {{ entry.merged.map(m => m.title).join(', ') }}
          <span class="text-medium-emphasis"> · {{ movedText(entry.moved) }}<template v-if="entry.userName"> · {{ entry.userName }}</template></span>
        </v-list-item>
      </v-list>
    </card>

    <!-- Two or more ticked: the way to the merge dialog. -->
    <div v-if="selected.length >= 2" class="data-merge__bar">
      <div class="data-merge__bar-in">
        <span>{{ selected.length }} rida valitud: {{ selectedPreview }}</span>
        <v-btn color="green" @click="openMerge">Ühenda…</v-btn>
      </div>
    </div>

    <v-dialog v-model="dialog" max-width="580" scrollable>
      <v-card v-if="dialog">
        <v-card-title class="text-h5">Ühenda {{ city ? 'linnad' : 'riigid' }}</v-card-title>
        <v-card-text>
          <p class="text-body-2 text-medium-emphasis mb-3">Vali, milline jääb alles. Teised kustutatakse pärast seda, kui nende read on üle viidud.</p>
          <v-radio-group v-model="keepId" hide-details @update:model-value="keepTitle = byId(keepId).title">
            <div
              v-for="row in selectedByUse"
              :key="row.id"
              class="data-merge__pick"
              :class="{ 'data-merge__pick--keep': row.id === keepId }"
              @click="keepId = row.id; keepTitle = row.title"
            >
              <v-radio :value="row.id" density="compact" hide-details :disabled="protectedSelected && row.id !== protectedId" />
              <div class="flex-grow-1" style="min-width: 0">
                {{ row.title }}<span v-if="city" class="text-medium-emphasis"> · {{ row.countryTitle }}</span>
                <div class="data-merge__what">{{ row.id === keepId ? 'jääb alles' : 'kustutatakse' }}</div>
              </div>
              <div class="text-caption text-medium-emphasis text-no-wrap">{{ row.clients }} klienti · {{ row.objects }} obj.<template v-if="!city"> · {{ row.cities }} linna</template></div>
            </div>
          </v-radio-group>
          <p v-if="protectedSelected" class="text-caption text-medium-emphasis mt-2 mb-0">{{ byId(protectedId).title }} on vaikimisi väärtus ja jääb alati alles.</p>
          <v-text-field v-model="keepTitle" label="Alles jääva nimi (võid parandada)" class="mt-4" density="compact" hide-details />
          <div class="data-merge__sum mt-4">
            Liigub <b>{{ moving.clients }}</b> klienti ja <b>{{ moving.objects }}</b> objekti<template v-if="!city"> ning <b>{{ moving.cities }}</b> linna</template>
            → <b>{{ keepTitle.trim() || byId(keepId).title }}</b><template v-if="city"> (riik {{ byId(keepId).countryTitle }})</template>.
            Kustutatakse {{ selected.length - 1 }} {{ city ? (selected.length - 1 === 1 ? 'linn' : 'linna') : (selected.length - 1 === 1 ? 'riik' : 'riiki') }}.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialog = false">Tühista</v-btn>
          <v-btn color="green" variant="flat" :loading="merging" @click="merge">Ühenda</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </system-frame>
</template>

<script>
import SystemFrame from '@/components/SystemFrame.vue'
import api from '@/api'
import dayjs from 'dayjs'
import { mapActions } from 'vuex'
import { suggestions } from './dataKeys'

export default {
  components: { SystemFrame },
  props: {
    // 'city' or 'country', from the route
    kind: { type: String, required: true }
  },
  data () {
    return {
      rows: [],
      recent: [],
      protectedId: 1,
      loaded: false,
      search: '',
      onlyUnused: false,
      limit: 50,
      picked: new Set(),
      chip: null,
      dialog: false,
      keepId: null,
      keepTitle: '',
      merging: false,
      renaming: null,
      renameTitle: '',
      deleting: null,
      // After a country merge: how many city suggestions there are now.
      cityHint: null
    }
  },
  computed: {
    city () {
      return this.kind === 'city'
    },
    groups () {
      return suggestions(this.rows, this.kind)
    },
    unusedCount () {
      return this.rows.filter(row => !this.isUsed(row)).length
    },
    filtered () {
      const q = (this.search || '').trim().toLowerCase()
      return this.rows.filter(row => (!q || row.title.toLowerCase().includes(q)) && (!this.onlyUnused || !this.isUsed(row)))
    },
    // Ticked rows first, so a suggestion's group is in view.
    shown () {
      return this.filtered.slice().sort((a, b) => this.picked.has(b.id) - this.picked.has(a.id)).slice(0, this.limit)
    },
    selected () {
      return this.rows.filter(row => this.picked.has(row.id))
    },
    selectedByUse () {
      return this.selected.slice().sort((a, b) => this.weight(b) - this.weight(a))
    },
    selectedPreview () {
      const names = this.selected.slice(0, 3).map(row => row.title).join(', ')
      return this.selected.length > 3 ? names + '…' : names
    },
    protectedSelected () {
      return this.picked.has(this.protectedId)
    },
    moving () {
      const rest = this.selected.filter(row => row.id !== this.keepId)
      const sum = key => rest.reduce((n, row) => n + (row[key] || 0), 0)
      return { clients: sum('clients'), objects: sum('objects'), cities: sum('cities') }
    }
  },
  watch: {
    kind () {
      this.reset()
      this.load()
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    async load () {
      try {
        const data = await api.getSystemData(this.kind)
        this.rows = data.rows
        this.recent = data.recent
        this.protectedId = data.protectedId
        this.loaded = true
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
    },
    reset () {
      this.rows = []
      this.recent = []
      this.loaded = false
      this.search = ''
      this.limit = 50
      this.clearSelection()
      this.cityHint = null
    },
    isUsed (row) {
      return row.clients + row.objects + (row.cities || 0) > 0
    },
    weight (row) {
      return row.clients + row.objects + (row.cities || 0)
    },
    byId (id) {
      return this.rows.find(row => row.id === id) || {}
    },
    canDelete (row) {
      return !this.isUsed(row) && row.id !== this.protectedId
    },
    toggle (row, on) {
      const picked = new Set(this.picked)
      on ? picked.add(row.id) : picked.delete(row.id)
      this.picked = picked
      this.chip = null
    },
    pickGroup (group) {
      this.picked = new Set(group.map(row => row.id))
      this.chip = group[0].id
    },
    clearSelection () {
      this.picked = new Set()
      this.chip = null
    },
    openMerge () {
      // The default row can only stay; otherwise the most used one.
      const keep = this.protectedSelected ? this.byId(this.protectedId) : this.selectedByUse[0]
      this.keepId = keep.id
      this.keepTitle = keep.title
      this.dialog = true
    },
    async merge () {
      this.merging = true
      try {
        const citySuggestionsBefore = this.city ? null : await this.citySuggestionCount()
        const mergeIds = this.selected.filter(row => row.id !== this.keepId).map(row => row.id)
        const result = await api.mergeSystemData(this.kind, { keepId: this.keepId, mergeIds, title: this.keepTitle })
        this.dialog = false
        this.clearSelection()
        this.showNotification({ text: 'Ühendatud: ' + result.merged.map(m => m.title).join(', ') + ' → ' + result.keepTitle, color: 'success' })
        await this.load()
        if (!this.city) {
          const after = await this.citySuggestionCount()
          if (after > citySuggestionsBefore) {
            this.cityHint = (after - citySuggestionsBefore) + ' linna gruppi said duplikaadiks (kokku ' + after + ' soovitust).'
          }
        }
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.merging = false
      }
    },
    async citySuggestionCount () {
      const data = await api.getSystemData('city')
      return suggestions(data.rows, 'city').length
    },
    startRename (row) {
      this.renaming = row.id
      this.renameTitle = row.title
    },
    async saveRename (row) {
      const title = (this.renameTitle || '').trim()
      this.renaming = null
      if (!title || title === row.title) return
      try {
        const result = await api.renameSystemData(this.kind, row.id, title)
        row.title = result.title
        this.showNotification({ text: 'Nimi muudetud: ' + result.before + ' → ' + result.title, color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
    },
    async remove (row) {
      this.deleting = row.id
      try {
        await api.deleteSystemData(this.kind, row.id)
        this.rows = this.rows.filter(r => r.id !== row.id)
        this.toggle(row, false)
        this.showNotification({ text: 'Kustutatud: ' + row.title, color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.deleting = null
      }
    },
    movedText (moved) {
      const parts = [moved.clients + ' klienti', moved.objects + ' objekti']
      if (moved.cities != null) parts.push(moved.cities + ' linna')
      return parts.join(', ')
    },
    dateTime (date) {
      return date ? dayjs(date).format('DD.MM.YYYY HH:mm') : ''
    }
  },
  mounted () {
    this.load()
  }
}
</script>

<style scoped>
.data-merge { padding-bottom: 96px; }
.data-merge__sel td { background: rgba(46, 125, 50, .08); }
.data-merge__table td { font-variant-numeric: tabular-nums; }
.data-merge__bar { position: fixed; left: 0; right: 0; bottom: 16px; display: flex; justify-content: center; pointer-events: none; z-index: 5; padding: 0 16px; }
.data-merge__bar-in { pointer-events: auto; background: #16322A; color: #fff; border-radius: 10px; padding: 8px 10px 8px 18px; display: flex; flex-wrap: wrap; gap: 14px; align-items: center; box-shadow: 0 6px 24px rgba(0, 0, 0, .25); max-width: 100%; }
.data-merge__pick { display: flex; gap: 10px; align-items: center; padding: 6px 10px; border: 1px solid rgba(0, 0, 0, .12); border-radius: 6px; margin-bottom: 6px; cursor: pointer; }
.data-merge__pick--keep { border-color: #2E7D32; background: #EAF3EA; }
.data-merge__what { font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; color: #B3261E; }
.data-merge__pick--keep .data-merge__what { color: #1B5E20; }
.data-merge__sum { background: rgba(0, 0, 0, .04); border-radius: 6px; padding: 10px 12px; font-size: 14px; }
</style>
